import { useNavigate } from "react-router-dom";
import { ArrowRight, Mic, Film, Captions, Music2, MonitorPlay, Smartphone, ShieldOff, Layers } from "lucide-react";
import { useEffect, useState } from "react";
import { Navbar, Logo } from "../components/Navbar";
import { API, api } from "../lib/api";
import { useTitle } from "../lib/seo";
import { Pricing } from "../components/Pricing";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const H_IMG = "https://images.unsplash.com/photo-1477346611705-65d1883cee1e?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";
const V_IMG = "https://images.unsplash.com/photo-1601042879364-f3947d3f9c16?crop=entropy&cs=srgb&fm=jpg&q=80&w=700";

const Hero = () => {
  const nav = useNavigate();
  return (
    <section className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-28 grid lg:grid-cols-12 gap-14 items-center">
      <div className="aurora" />
      <div className="lg:col-span-6 fade-up relative">
        <span className="inline-flex items-center gap-2 text-xs font-mono-tc text-[#E8C47C] border border-[#E8C47C]/30 rounded-full px-3 py-1.5" data-testid="hero-badge">
          <ShieldOff className="h-3.5 w-3.5" /> IA para automatizar · Producción audiovisual inteligente
        </span>
        <h1 className="mt-7 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
          Tu guion se convierte en <span className="gold-text italic">video listo</span> para YouTube y TikTok.
        </h1>
        <p className="mt-6 text-base md:text-lg text-zinc-400 max-w-xl leading-relaxed">
          Escribe un guion o sube tu narración. La automatización inteligente de ArCreativeStudio organiza el contenido en escenas, busca clips relacionados en Pexels y Pixabay, integra voces Piper, música, transiciones y subtítulos. Exporta en formatos 16:9 y 9:16.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <Button data-testid="hero-cta-start" onClick={() => nav("/register")} className="rounded-full bg-[#E8C47C] hover:bg-[#F3D9A4] text-black font-semibold h-12 px-7 text-base">
            Crear mi video de prueba <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          <Button data-testid="hero-cta-pricing" variant="ghost" onClick={() => document.getElementById("precios").scrollIntoView({ behavior: "smooth" })} className="rounded-full h-12 px-7 text-zinc-300 border border-white/10">
            Ver planes
          </Button>
        </div>
        <div className="mt-12 flex gap-10 text-sm">
          {[["178", "voces Piper"], ["8", "estilos de locución"], ["20+10", "músicas y SFX"]].map(([n, l]) => (
            <div key={l}><p className="font-display text-2xl font-bold text-white">{n}</p><p className="text-zinc-500">{l}</p></div>
          ))}
        </div>
      </div>
      <div className="lg:col-span-6 relative h-[440px] fade-up" style={{ animationDelay: "150ms" }} data-testid="hero-mockup">
        <div className="absolute left-0 top-6 w-[86%] aspect-video rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
          <img src={H_IMG} alt="Video 16:9" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent" />
          <span className="absolute top-3 left-3 text-[10px] font-mono-tc bg-black/60 px-2 py-1 rounded text-zinc-200 flex items-center gap-1"><MonitorPlay className="h-3 w-3" />YouTube · 16:9</span>
          <p className="absolute bottom-5 inset-x-0 text-center font-anton text-2xl tracking-wide text-white uppercase">las montañas <span className="word-pop inline-block mx-2">guardan</span> secretos</p>
        </div>
        <div className="absolute right-0 bottom-0 w-[38%] aspect-[9/16] rounded-2xl overflow-hidden border border-[#E8C47C]/40 glow">
          <img src={V_IMG} alt="Video 9:16" className="h-full w-full object-cover" />
          <span className="absolute top-3 left-3 text-[10px] font-mono-tc bg-black/60 px-2 py-1 rounded text-zinc-200 flex items-center gap-1"><Smartphone className="h-3 w-3" />TikTok · 9:16</span>
          <p className="absolute bottom-[22%] inset-x-2 text-center font-anton text-xl text-white uppercase leading-tight">la ciudad <span className="word-pop inline-block mx-2" style={{ animationDelay: ".8s" }}>nunca</span> duerme</p>
        </div>
        <div className="absolute left-6 bottom-2 panel px-4 py-3 flex items-center gap-3 text-xs">
          <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="font-mono-tc text-zinc-300">RENDER 00:42 · 1080p · H.264</span>
        </div>
      </div>
    </section>
  );
};

const Samples = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/samples").then((r) => setItems(r.data)).catch(() => {}); }, []);
  if (!items.length) return null;
  return (
    <section id="muestras" className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="samples-section">
      <p className="font-mono-tc text-xs text-[#E8C47C] tracking-widest">VIDEOS DE MUESTRA</p>
      <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl text-white max-w-2xl">Videos preparados mediante un flujo de producción audiovisual automatizado.</h2>
      <div className="mt-12 flex flex-wrap gap-6 items-end">
        {items.map((v) => (
          <figure key={v.id} className={`panel overflow-hidden p-2 ${v.aspect === "9:16" ? "w-[260px]" : "w-full md:w-[calc(100%-290px)] max-w-[760px]"}`} data-testid={`sample-${v.id}`}>
            <video src={`${API}/samples/${v.id}/mp4`} poster={`${API}/samples/${v.id}/jpg`} controls muted loop playsInline preload="none"
              className={`w-full rounded-xl bg-black ${v.aspect === "9:16" ? "aspect-[9/16]" : "aspect-video"}`} />
            <figcaption className="px-2 py-3 flex justify-between text-xs"><span className="text-zinc-300">{v.title}</span><span className="font-mono-tc text-zinc-500">{v.aspect} · {Math.round(v.duration)}s</span></figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
};

const FEATS = [
  { icon: Mic, t: "Voces Piper TTS", d: "Catálogo oficial completo (178 voces, 58 idiomas/países) con 8 estilos de locución: documental, cine, serie, reflexión, poesía, comercial y noticias.", span: "md:col-span-2" },
  { icon: Film, t: "Clips de Pexels y Pixabay", d: "Cada frase se analiza (quién, qué hace, dónde) para buscar el clip exacto en Pexels y, si no hay, en Pixabay." },
  { icon: Captions, t: "Subtítulos TikTok", d: "Palabra resaltada, fuentes Anton/Bebas/Poppins y exportación SRT." },
  { icon: Music2, t: "Música libre de derechos", d: "20 pistas originales CC0 y 10 efectos de sonido para captar la atención." },
  { icon: Layers, t: "Efectos de cine", d: "Ken Burns, 10 transiciones, filtros de color cinematográficos y videos cortos o largos (hasta 60 min).", span: "md:col-span-2" },
];

const FAQ = [
  ["¿Cómo utiliza inteligencia artificial ArCreativeStudio?", "ArCreativeStudio utiliza automatización inteligente para analizar y organizar el contenido audiovisual. El video se compone con clips de Pexels y Pixabay, voces Piper TTS, música y subtítulos; no genera directamente las escenas mediante modelos de video generativo."],
  ["¿Puedo subir mi propia narración?", "Sí. En los planes de pago puedes subir un audio (MP3, WAV, M4A…) creado con cualquier app de TTS o grabado por ti, y el sistema sincroniza clips y subtítulos."],
  ["¿La música tiene copyright?", "No. La biblioteca incluida es original y CC0. Los clips de Pexels son de uso gratuito según su licencia. También puedes subir pistas propias."],
  ["¿Cómo funciona la prueba gratis?", "Cada cuenta puede generar 1 video de prueba de hasta 15 segundos con marca de agua para comprobar la calidad."],
  ["¿Qué métodos de pago aceptan?", "Aceptamos pagos seguros mediante PayPal. Puedes elegir entre planes mensuales y anuales, con 15% de descuento en el pago anual."],
];

export default function Landing({ section }) {
  const SEO_PAGES = {
  inicio: {
    title: "ArCreativeStudio | Creador automático de videos con IA para redes sociales",
    description: "Crea videos automáticamente para YouTube, TikTok, Instagram y Facebook. Automatización inteligente de guiones, clips, narración, música y subtítulos.",
    path: "/",
    keywords: "ArCreativeStudio, crear videos automáticamente, creador automático de videos, generador de videos con IA, automatización audiovisual, videos para redes sociales"
  },
  precios: {
    title: "Planes y precios | ArCreativeStudio",
    description: "Conoce los planes de ArCreativeStudio para automatizar la producción de videos para YouTube, TikTok, Instagram, Facebook y otros canales.",
    path: "/precios",
    keywords: "precios creador de videos, planes para crear videos, software de videos, herramienta para creadores, ArCreativeStudio"
  },
  muestras: {
    title: "Videos de muestra | ArCreativeStudio",
    description: "Explora ejemplos de videos horizontales y verticales preparados con automatización inteligente, narraciones, clips y subtítulos.",
    path: "/muestras",
    keywords: "ejemplos de videos, muestras de videos para YouTube, muestras de Reels, videos TikTok, videos automatizados"
  },
  funciones: {
    title: "Funciones de creación automática de videos | ArCreativeStudio",
    description: "Descubre cómo organizar guiones, buscar clips, integrar voces, música y subtítulos para crear videos de redes sociales con automatización inteligente.",
    path: "/funciones",
    keywords: "funciones creador de videos, convertir guion en video, convertir audio en video, videos con narración, subtítulos automáticos, automatización de videos"
  }
};

const seo = SEO_PAGES[section] || SEO_PAGES.inicio;
useTitle(seo.title, seo.description, seo.path, seo.keywords);
  useEffect(() => { if (section) setTimeout(() => document.getElementById(section)?.scrollIntoView({ behavior: "smooth" }), 400); }, [section]);
  return (
    <div className="grain min-h-screen bg-[#09090b]" data-testid="landing-page">
      <Navbar landing />
      <Hero />
      <section id="funciones" className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <p className="font-mono-tc text-xs text-[#E8C47C] tracking-widest">FUNCIONES</p>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-white max-w-2xl">Un estudio de producción completo, sin editor de video.</h2>
        <div className="mt-14 grid md:grid-cols-4 gap-5">
          {FEATS.map(({ icon: I, t, d, span }, i) => (
            <div key={t} className={`panel p-7 fade-up hover:border-[#E8C47C]/30 transition-colors ${span || ""}`} style={{ animationDelay: `${i * 80}ms` }} data-testid={`feature-${i}`}>
              <I className="h-6 w-6 text-[#E8C47C]" />
              <h3 className="mt-5 text-lg font-semibold text-white">{t}</h3>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>
      <Samples />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-10">
          {[["01", "Escribe o sube", "Pega tu guion o sube una narración. Se divide en escenas automáticamente."], ["02", "Personaliza", "Formato, voz, música, transiciones, filtros y estilo de subtítulos."], ["03", "Renderiza", "Descarga tu MP4 en HD y el archivo SRT, listo para publicar."]].map(([n, t, d]) => (
            <div key={n} className="border-t border-white/10 pt-6">
              <p className="font-mono-tc text-[#E8C47C] text-sm">{n}</p>
              <h3 className="mt-3 text-xl font-semibold text-white">{t}</h3>
              <p className="mt-2 text-sm text-zinc-400">{d}</p>
            </div>
          ))}
        </div>
      </section>
      <section id="precios" className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <p className="font-mono-tc text-xs text-[#E8C47C] tracking-widest text-center">PRECIOS</p>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-white text-center">Planes para cada creador</h2>
        <p className="text-zinc-400 text-center mt-3 mb-10">Paga mensual o ahorra un 15% con la suscripción anual.</p>
        <Pricing />
      </section>
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-2 gap-12" data-testid="seo-section">
        <div>
          <h2 className="text-2xl sm:text-3xl text-white">Crea videos mediante automatización inteligente con IA</h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">ArCreativeStudio facilita la producción audiovisual mediante automatización inteligente con IA. Puedes convertir un guion o una narración en un video organizado por escenas: el sistema analiza el contenido, busca clips relacionados en Pexels y Pixabay, integra voces Piper, música y subtítulos sincronizados. Es una herramienta para creadores de contenido, influencers, emprendedores, empresas y agencias que necesitan preparar videos para YouTube, TikTok, Instagram Reels, Facebook y Shorts.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {[["Videos para YouTube", "Formato 16:9 hasta 2K, ideal para documentales, tutoriales y canales faceless."], ["Videos para TikTok", "Formato vertical 9:16 con subtítulos dinámicos que retienen la atención."], ["Videos para Instagram", "Reels verticales con música libre de derechos y títulos elegantes."], ["Videos para Facebook", "Videos horizontales o verticales listos para publicar y promocionar."]].map(([t, d]) => (
            <article key={t} className="panel p-5"><h3 className="text-white text-base">{t}</h3><p className="text-sm text-zinc-400 mt-2">{d}</p></article>
          ))}
        </div>
      </section>
      <section id="faq" className="max-w-3xl mx-auto px-5 lg:px-8 py-24">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8">Preguntas frecuentes</h2>
        <Accordion type="single" collapsible data-testid="faq-accordion">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={q} value={`f${i}`} className="border-white/10">
              <AccordionTrigger data-testid={`faq-item-${i}`} className="text-left text-white hover:no-underline">{q}</AccordionTrigger>
              <AccordionContent className="text-zinc-400">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
      <footer className="border-t border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-wrap gap-6 justify-between items-center text-sm text-zinc-500">
          <Logo />
          <p>Clips de video por <a href="https://www.pexels.com" target="_blank" rel="noreferrer" className="text-zinc-300 hover:text-white">Pexels</a> · Voces <a href="https://github.com/OHF-Voice/piper1-gpl" target="_blank" rel="noreferrer" className="text-zinc-300 hover:text-white">Piper</a></p>
          <p>© {new Date().getFullYear()} ArCreativeStudio · Creado por ARIS Y RUBEN. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
